import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Typography, type TypographyProps } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { DoneBranchGroup } from '../../doneImports';
import { ImportRow, SubtotalRow } from './BranchImportRows';

type BranchImportsProps = {
    group: DoneBranchGroup;
    /** A phone: two columns, the file with its figures under it, and the value. */
    isNarrow: boolean;
};

// A branch's name heads its list, under the step's title.
const asH2: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h2 {...domProps}>{children}</h2>;

/**
 * One branch's imports as a HeroUI `Table`, newest first: file, date, rows,
 * products created, units and the stock's net value, then the branch's
 * total as the last row of the body (a real row, so it lines up with the
 * columns and a screen reader reaches it; the accounting tables' precedent).
 *
 * An undone import stays in the list, muted, with an "Undone" chip; the
 * total leaves it out. Fixed column widths keep every branch's columns in
 * line with the others'. On a phone the table keeps two columns: the file
 * with its date and figures under it, and the value.
 */
export const BranchImports: React.FC<BranchImportsProps> = ({
    group,
    isNarrow,
}) => {
    const { t } = useTranslation('pages');

    const meta = [
        t('onboarding.done.imports.count', {
            count: group.completedCount,
            total: countParam(group.completedCount),
        }),
        group.undoneCount > 0
            ? t('onboarding.done.imports.undoneCount', {
                  total: countParam(group.undoneCount),
              })
            : null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <div
            className="flex flex-col gap-3"
            {...getNodeTestIdProp(`onboarding-done-branch-${group.branchId}`)}
        >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <Typography render={asH2} type="h4">
                    {group.branchName}
                </Typography>
                <Typography
                    color="muted"
                    type="body-sm"
                    {...getNodeTestIdProp(
                        `onboarding-done-branch-meta-${group.branchId}`,
                    )}
                >
                    {meta}
                </Typography>
            </div>
            <Table>
                <Table.ScrollContainer>
                    <Table.Content
                        aria-label={t('onboarding.done.imports.tableAria', {
                            branch: group.branchName,
                        })}
                        className="table-fixed text-sm"
                    >
                        <Table.Header>
                            <Table.Column id="file" isRowHeader>
                                {t('onboarding.done.imports.columns.file')}
                            </Table.Column>
                            {isNarrow ? null : (
                                <Table.Column className="w-40" id="date">
                                    {t('onboarding.done.imports.columns.date')}
                                </Table.Column>
                            )}
                            {isNarrow ? null : (
                                <Table.Column
                                    className="w-28 text-end"
                                    id="rows"
                                >
                                    {t('onboarding.done.imports.columns.rows')}
                                </Table.Column>
                            )}
                            {isNarrow ? null : (
                                <Table.Column
                                    className="w-28 text-end"
                                    id="products"
                                >
                                    {t(
                                        'onboarding.done.imports.columns.products',
                                    )}
                                </Table.Column>
                            )}
                            {isNarrow ? null : (
                                <Table.Column
                                    className="w-28 text-end"
                                    id="units"
                                >
                                    {t('onboarding.done.imports.columns.units')}
                                </Table.Column>
                            )}
                            <Table.Column
                                className={cn(
                                    'text-end',
                                    isNarrow ? 'w-32' : 'w-44',
                                )}
                                id="stockValue"
                            >
                                {t(
                                    'onboarding.done.imports.columns.stockValue',
                                )}
                            </Table.Column>
                        </Table.Header>
                        <Table.Body>
                            {[
                                ...group.imports.map((item) => (
                                    <ImportRow
                                        isNarrow={isNarrow}
                                        item={item}
                                        key={item.id}
                                    />
                                )),
                                <SubtotalRow
                                    branchId={group.branchId}
                                    figures={group.subtotal}
                                    isNarrow={isNarrow}
                                    key="subtotal"
                                />,
                            ]}
                        </Table.Body>
                    </Table.Content>
                </Table.ScrollContainer>
            </Table>
        </div>
    );
};
